/**
 * WebSocket Provider
 * Handles WebSocket connection, authentication, and message routing.
 * Exposes granular connection health (state, latency, reconnect info)
 * so UI components can show real-time connection status to the user.
 */

import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import { getRuntimeConfig } from "../config/runtime";
import { authenticateWebSocket as authWsFn } from "../services/authService";
import type { ConnectionHealth } from "../services/socketService";
import socketServiceDefault from "../services/socketService";
import { useCognito } from "./CognitoProvider";

// Service references (initialized on first use in real mode)
let socketService: any = socketServiceDefault;
let authenticateWebSocket: any = authWsFn;

const DEFAULT_HEALTH: ConnectionHealth = {
  state: "disconnected",
  latencyMs: null,
  reconnectAttempt: 0,
  maxReconnectAttempts: 5,
  reconnectInSeconds: null,
  isNetworkOnline: true,
  lastPingReceivedAt: null,
  connectedSince: null,
  isLatencyElevated: false,
};

export interface WebSocketContextType {
  isConnected: boolean;
  isAuthenticated: boolean;
  error: string | null;
  /** Granular connection health for UI status indicators */
  health: ConnectionHealth;
  connect: (idToken: string) => Promise<any>;
  disconnect: () => void;
  reconnect: () => void;
}

const WebSocketContext = createContext<WebSocketContextType | undefined>(
  undefined,
);

export const WebSocketProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [isConnected, setIsConnected] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [health, setHealth] = useState<ConnectionHealth>(DEFAULT_HEALTH);
  // Prevent concurrent or repeated auth calls
  const authInProgress = React.useRef(false);

  const config = getRuntimeConfig();
  const { isAuthenticated: userIsAuthenticated, tokens, logout } = useCognito();

  // Subscribe to health changes from SocketService
  useEffect(() => {
    if (!socketService?.onHealthChange) return;
    const unsub = socketService.onHealthChange((h: ConnectionHealth) => {
      setHealth(h);
    });
    return unsub;
  }, []);

  // Define connect function with useCallback so it can be referenced in useEffects
  const connect = React.useCallback(
    async (idToken: string) => {
      try {
        socketService?.markAuthenticating?.();
        // Authenticate WebSocket with JWT
        const authResponse = await authenticateWebSocket(idToken);
        setIsAuthenticated(true);
        setError(null);
        socketService?.markAuthenticated?.();
        return authResponse;
      } catch (err: any) {
        const message = err?.message || "WebSocket authentication failed";
        setError(message);
        setIsAuthenticated(false);
        throw err;
      }
    },
    [config?.mode],
  );

  // Initialize WebSocket listeners (only in real mode AFTER authentication)
  useEffect(() => {
    // Only set up listeners if user is authenticated
    if (!userIsAuthenticated) {
      return;
    }

    // Register callbacks after authentication
    socketService.onStatusChange({
      onConnected: () => {
        setIsConnected(true);
        setError(null);
      },
      onDisconnected: () => {
        setIsConnected(false);
        setIsAuthenticated(false);
        authInProgress.current = false;
      },
      onError: (err: Error) => {
        setError(err.message);
      },
      onReconnecting: (_attempt: number) => {},
      onLogout: () => {
        // All reconnect attempts exhausted — force logout
        socketService?.disconnect();
        logout();
      },
    });

    return () => {
      if (socketService) {
        socketService.onStatusChange({});
      }
    };
  }, [config?.mode, userIsAuthenticated]);

  // Disconnect WebSocket when user logs out — stops all reconnection attempts
  useEffect(() => {
    if (userIsAuthenticated) return;
    socketService?.disconnect();
    setIsConnected(false);
    setIsAuthenticated(false);
    authInProgress.current = false;
  }, [userIsAuthenticated]);

  // Connect WebSocket: only after authentication
  useEffect(() => {
    if (!userIsAuthenticated || !tokens?.idToken) return;
    socketService?.connect();
  }, [userIsAuthenticated, tokens?.idToken, config?.mode]);

  // Authenticate WebSocket after connection is established
  // This handles both fresh login AND token restoration on page refresh
  useEffect(() => {
    // Only authenticate after socket is connected AND we have tokens
    if (!isConnected || !userIsAuthenticated || !tokens?.idToken) {
      return;
    }

    // Skip if already authenticated or auth is in progress
    if (isAuthenticated || authInProgress.current) {
      return;
    }

    authInProgress.current = true;
    connect(tokens.idToken)
      .catch((_err) => {})
      .finally(() => {
        authInProgress.current = false;
      });
  }, [
    isConnected,
    userIsAuthenticated,
    tokens?.idToken,
    isAuthenticated,
    config?.mode,
    connect,
  ]);

  const disconnect = () => {
    if (socketService) {
      socketService.disconnect();
    }
    setIsConnected(false);
    setIsAuthenticated(false);
    setError(null);
  };

  const reconnect = () => {
    if (socketService) {
      socketService.reconnect();
    }
  };

  return (
    <WebSocketContext.Provider
      value={{
        isConnected,
        isAuthenticated,
        error,
        health,
        connect,
        disconnect,
        reconnect,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = (): WebSocketContextType => {
  const context = useContext(WebSocketContext);
  if (!context) {
    // Return a safe no-op default so components (e.g. ConnectionIndicator)
    // render gracefully outside the provider tree.
    return {
      isConnected: false,
      isAuthenticated: false,
      error: null,
      health: DEFAULT_HEALTH,
      connect: () => Promise.resolve(),
      disconnect: () => {},
      reconnect: () => {},
    };
  }
  return context;
};
